/**
 * Reflects an unread count in the browser tab chrome — the document title and
 * the favicon — so a new message is visible even when the tab is backgrounded
 * and the in-app toast/header badge are out of view.
 *
 * The pure formatting helpers are unit-tested; the DOM/canvas plumbing below
 * them is a thin, idempotent side-effect layer.
 */

/** Strip a leading "(12) " unread badge so we can recover a clean base title. */
export function stripUnreadPrefix(title: string): string {
  return title.replace(/^\(\d+\+?\)\s+/, '')
}

/** `"(3) FlagFinder"` when unread, otherwise the base title unchanged. */
export function formatUnreadTitle(baseTitle: string, count: number): string {
  if (count <= 0) return baseTitle
  const label = count > 99 ? '99+' : String(count)
  return `(${label}) ${baseTitle}`
}

// --- DOM side effects -------------------------------------------------------

interface IconSnapshot {
  link: HTMLLinkElement
  originalHref: string
}

let baseTitle: string | null = null
let iconSnapshots: IconSnapshot[] | null = null
let badgedHref: string | null = null
let baseImagePromise: Promise<HTMLImageElement | null> | null = null
let wantBadge = false

function captureBaseTitle(): string {
  if (baseTitle === null) {
    baseTitle = stripUnreadPrefix(document.title)
  }
  return baseTitle
}

function ensureSnapshots(): IconSnapshot[] {
  if (iconSnapshots === null) {
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']"),
    )
    iconSnapshots = links.map((link) => ({ link, originalHref: link.href }))
  }
  return iconSnapshots
}

/** Prefer a PNG icon we can draw onto a canvas; a .ico is not reliably drawable. */
function pickSourceHref(snapshots: IconSnapshot[]): string | null {
  const png = snapshots.find((s) => /\.png(\?|$)/i.test(s.originalHref))
  return (png ?? snapshots[0])?.originalHref ?? null
}

function loadImage(href: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = href
  })
}

/** Draw the base favicon with a red notification dot in the top-right corner. */
function renderBadgedFavicon(img: HTMLImageElement): string | null {
  const size = 32
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  ctx.drawImage(img, 0, 0, size, size)

  const radius = 8
  const cx = size - radius - 1
  const cy = radius + 1
  ctx.beginPath()
  ctx.arc(cx, cy, radius, 0, Math.PI * 2)
  ctx.fillStyle = '#ef4444' // red-500
  ctx.fill()
  ctx.lineWidth = 2
  ctx.strokeStyle = '#ffffff'
  ctx.stroke()

  try {
    return canvas.toDataURL('image/png')
  } catch {
    // Tainted canvas (unexpected for same-origin icons) — skip the favicon.
    return null
  }
}

async function buildBadgedHref(): Promise<string | null> {
  if (badgedHref !== null) return badgedHref
  const src = pickSourceHref(ensureSnapshots())
  if (!src) return null
  if (baseImagePromise === null) baseImagePromise = loadImage(src)
  const img = await baseImagePromise
  if (!img) return null
  badgedHref = renderBadgedFavicon(img)
  return badgedHref
}

function applyBadgedFavicon(): void {
  wantBadge = true
  buildBadgedHref().then((href) => {
    // Bail if the count dropped to zero while the image was loading.
    if (!href || !wantBadge || iconSnapshots === null) return
    for (const snapshot of iconSnapshots) {
      snapshot.link.href = href
    }
  })
}

function restoreFavicon(): void {
  wantBadge = false
  if (iconSnapshots === null) return
  for (const snapshot of iconSnapshots) {
    snapshot.link.href = snapshot.originalHref
  }
}

/** Reflect the current unread count in the tab title and favicon. */
export function setUnreadIndicator(count: number): void {
  document.title = formatUnreadTitle(captureBaseTitle(), count)
  if (count > 0) {
    applyBadgedFavicon()
  } else {
    restoreFavicon()
  }
}

/** Restore the pristine title and favicon (call on teardown). */
export function resetUnreadIndicator(): void {
  if (baseTitle !== null) document.title = baseTitle
  restoreFavicon()
}
